/* TDB · One-click Production Bootstrap tile (CEO-only)
 *
 * Single button that hits POST /api/tdb-mis/ingredient-master/admin/bootstrap-prod
 * and renders per-step status pills + a final-state summary. Idempotent —
 * safe to click any time (e.g. fresh staging environment · post-restore).
 *
 * Hidden entirely for non-CEO viewers (the endpoint 403s, the tile
 * just doesn't render).
 *
 * Steps surfaced:
 *   1. Doctrine collections (3 consultants · 5 shared cost · 7 routing)
 *   2. Expense categories (37)
 *   3. Stock items master (85 protocol + 3 placeholders)
 *   4. Recipe pack v2 (3,741 SKUs · 750 bundles · 97 ingredients)
 *   5. April 2026 MIS (closed-month seed)
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Database, Loader2, CheckCircle2, XCircle, Rocket } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_DEFS = [
  {
    key: 'doctrine',
    label: 'Doctrine collections',
    detail: (s) =>
      `${s.consultants_total || 0} consultants · ${s.shared_costs_total || 0} shared cost · ${s.routing_rules_total || 0} routing`,
  },
  {
    key: 'categories',
    label: 'Expense categories',
    detail: (s) => `${s.total || 0} / ${s.expected || 37} categories`,
  },
  {
    key: 'stock_items',
    label: 'Stock items master',
    detail: (s) =>
      `${s.expected || 88} expected · ${s.breakdown?.raw || 0} raw · ${s.breakdown?.packaging || 0} packaging · ${s.breakdown?.consumable || 0} consumable · ${s.breakdown?.other_placeholders || 0} placeholders`,
  },
  {
    key: 'recipe_pack_v2',
    label: 'Recipe pack v2',
    detail: (s) =>
      s?.skus
        ? `${s.skus.inserted || 0} SKUs · ${s.bundles?.inserted || 0} bundles · ${s.ingredient_master?.updated_existing || 0} ingredients · ${s.cake_overrides?.inserted || 0} cake overrides`
        : (s?.error || s?.reason || '—'),
  },
  {
    key: 'mis_april_2026',
    label: 'April 2026 MIS',
    detail: (s) =>
      s?.loaded
        ? `${s.period_start} → ${s.period_end} · layout ${s.layout || 'v4'} · ${s.upserted ? 'inserted' : 'updated'}`
        : (s?.error || s?.reason || '—'),
  },
];

const FINAL_STATE_DEFS = [
  { key: 'stock_items_active', label: 'Stock items active' },
  { key: 'ingredients_v2', label: 'Ingredients (v2)' },
  { key: 'recipe_skus_v2', label: 'Recipe SKUs (v2)' },
  { key: 'bundles_v2', label: 'Bundles / hampers (v2)' },
  { key: 'categories_total', label: 'Categories' },
  { key: 'mis_periods_total', label: 'MIS periods loaded' },
];

function _stepOk(stepKey, payload) {
  if (!payload) return false;
  if (payload.error) return false;
  if (payload.skipped) return false;
  if (stepKey === 'recipe_pack_v2') return !!payload.skus;
  if (stepKey === 'mis_april_2026') return !!payload.loaded;
  return true;
}

export default function TDBProdBootstrap({ user, className = '' }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Only the CEO sees this tile · the endpoint 403s otherwise but we hide
  // it client-side too so it doesn't show as a tease.
  if (!user || user.role !== 'ceo') return null;

  const runBootstrap = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/tdb-mis/ingredient-master/admin/bootstrap-prod`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120_000 },
      );
      setResult(res.data?.bootstrap || {});
      toast.success('Production bootstrap complete', {
        description: 'All 5 phases ran successfully. See the panel for counts.',
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
      setError(String(msg));
      toast.error('Bootstrap failed', { description: String(msg).slice(0, 240) });
    } finally {
      setRunning(false);
    }
  };

  const finalState = result?.final_state || {};

  return (
    <div
      data-testid="tdb-prod-bootstrap-tile"
      className={`rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-6 shadow-sm ${className}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-indigo-600 p-2.5 text-white">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-900">
              Bootstrap Production Data
            </div>
            <div className="text-sm text-slate-600 max-w-xl">
              Hydrate a fresh environment with doctrine, categories, stock items,
              recipe pack v2 (3,741 SKUs · 750 bundles), and April 2026 MIS.
              Idempotent · safe to click any time.
            </div>
          </div>
        </div>
        <button
          data-testid="tdb-prod-bootstrap-run-btn"
          onClick={runBootstrap}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {running ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Running…
            </>
          ) : (
            <>
              <Rocket className="h-4 w-4" />
              {result ? 'Re-run bootstrap' : 'Run bootstrap'}
            </>
          )}
        </button>
      </div>

      {error && (
        <div
          data-testid="tdb-prod-bootstrap-error"
          className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          ✗ {error}
        </div>
      )}

      {result && (
        <>
          <div className="space-y-2 mb-4">
            {STEP_DEFS.map(({ key, label, detail }) => {
              const payload = result[key] || {};
              const ok = _stepOk(key, payload);
              return (
                <div
                  key={key}
                  data-testid={`tdb-prod-bootstrap-step-${key}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
                >
                  {ok ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{label}</div>
                    <div className="text-xs text-slate-600 truncate">
                      {detail(payload)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 font-semibold">
              Final state on production
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {FINAL_STATE_DEFS.map(({ key, label }) => (
                <div
                  key={key}
                  data-testid={`tdb-prod-bootstrap-final-${key}`}
                  className="rounded-lg bg-slate-50 px-3 py-2"
                >
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-lg font-bold text-indigo-700">
                    {finalState[key] ?? '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!result && !error && (
        <div className="mt-3 text-xs text-slate-500">
          Click <strong>Run bootstrap</strong> after a fresh deploy to hydrate
          all TDB master data in one shot.
        </div>
      )}
    </div>
  );
}
